import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ArrowUpRight } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { MoleculePoster } from "./MoleculePoster";

// Product cards render an instant static SVG molecule poster — zero JS, zero network, zero wait.
// The live 3Dmol viewer runs only on the product detail page where users actually rotate/zoom.
// This makes the Shop + Home grids paint completely in the same frame as the surrounding text,
// removing the perceived "products are loading" pause on slower devices.

export const ProductCard = ({ product, index }) => {
    const { formatPrice } = useApp();
    const soldOut = product._variantCount ? (product._anyInStock === false) : (product.in_stock === false);
    const hasVariants = (product._variantCount || 0) > 1;
    const mol = product.molecule;
    const isPreorder = !!product.preorder;

    return (
        <Link
            to={`/product/${product.slug}`}
            className={`group product-card-hover border border-ink bg-ink-surface flex flex-col relative ${soldOut ? "opacity-70" : ""}`}
            data-testid={`product-card-${product.slug}`}
        >
            <div className="relative aspect-[4/5] overflow-hidden bg-ink-elevated border-b border-ink">
                {product.front_image ? (
                    <img
                        src={product.front_image}
                        alt={product.name}
                        loading="lazy"
                        decoding="async"
                        className="absolute inset-0 w-full h-full object-cover"
                        data-testid={`product-front-image-${product.slug}`}
                    />
                ) : (
                    <MoleculePoster molecule={mol || {}} className="w-full h-full" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-ink-base/40 via-transparent to-transparent pointer-events-none" />

                {soldOut && (
                    <div className="absolute top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-red-600 text-white overline text-[9px] font-bold z-10" data-testid="sold-out-badge">
                        SOLD OUT
                    </div>
                )}
                {!soldOut && isPreorder && (
                    <div className="absolute top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-amber-500 text-black overline text-[9px] font-bold z-10" data-testid="preorder-badge">
                        ◆ PRE-ORDER · 10–18 DAYS
                    </div>
                )}
                {!soldOut && !isPreorder && product.best_value && (
                    <div className="absolute top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gold text-black overline text-[9px] font-bold z-10">
                        ★ BEST VALUE
                    </div>
                )}
                {!soldOut && product.made_to_order && (
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-amber-500 text-black overline text-[10px] font-bold z-10 inline-flex items-center gap-1.5 shadow-[0_4px_16px_rgba(245,158,11,0.4)]" data-testid="mto-badge">
                        ⚒ MADE TO ORDER · {product.made_to_order_lead_time || "4–6 BUSINESS DAYS"}
                    </div>
                )}

                {typeof index === "number" && (
                    <div className="absolute top-3 left-3 overline text-[10px] text-ink-dim bg-ink-base/80 backdrop-blur px-2 py-1 z-10">
                        {String(index + 1).padStart(3, "0")}
                    </div>
                )}
                <div className="absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 px-2 py-1 bg-ink-base/80 backdrop-blur text-[10px] font-mono uppercase tracking-wider text-neon border border-neon/30">
                    <ShieldCheck className="w-3 h-3" /> COA
                </div>
                {product.is_new_arrival && (
                    <div className="absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 px-2.5 py-1 bg-neon text-black text-[10px] font-bold tracking-wider font-mono uppercase shadow-[0_0_24px_rgba(16,185,129,0.4)]" data-testid="new-arrival-badge">
                        ★ NEW
                    </div>
                )}
                {product.bulk_kit && (
                    <div className="absolute bottom-3 left-3 z-10 inline-flex items-center gap-1.5 px-3 py-1.5 bg-gold text-black font-display text-sm font-bold tracking-wide" data-testid="vials-count-badge">
                        ◆ 10-VIAL BULK KIT
                    </div>
                )}
                <div className="absolute bottom-3 right-3 z-10 p-2 bg-neon text-black opacity-0 group-hover:opacity-100 transition-opacity">
                    <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
                </div>
            </div>
            <div className="p-5 flex flex-col flex-1">
                <div className="flex items-start justify-between gap-2 mb-3">
                    <span className="overline text-ink-mute">{product.category}</span>
                    {hasVariants ? (
                        <span className="font-mono text-[11px] text-ink-dim">{product._variantCount} SIZES</span>
                    ) : (
                        <span className="inline-flex items-center gap-1 font-mono text-[11px] font-bold text-neon bg-neon/10 border border-neon/40 px-2 py-0.5" data-testid="product-dosage-badge">
                            {product.dosage}
                        </span>
                    )}
                </div>
                <h3 className="font-display text-xl font-bold text-ink leading-tight">{product.name.replace(/\s*\(.*?\)\s*$/, "")}</h3>
                {product.tag && (
                    <span className={`inline-flex items-center gap-1.5 mt-2 font-mono text-[9px] font-bold uppercase tracking-wider w-fit ${product.tag_color === "gold" ? "text-gold" : "text-neon"}`} data-testid="product-classification-tag">
                        <span className={`w-1 h-1 rounded-full ${product.tag_color === "gold" ? "bg-gold" : "bg-neon"}`} />
                        {product.tag}
                    </span>
                )}
                <p className="text-sm text-ink-dim mt-3 leading-relaxed line-clamp-2 flex-1">{product.short}</p>
                {hasVariants && (
                    <div className="mt-3 flex flex-wrap gap-1">
                        {product._variantLabels.map((v, i) => (
                            <span key={i} className="text-[10px] font-mono px-1.5 py-0.5 border border-ink text-ink-dim">{v}</span>
                        ))}
                    </div>
                )}
                <div className="mt-5 pt-4 border-t border-ink flex items-end justify-between">
                    <div>
                        {hasVariants && <p className="overline text-[9px] text-ink-mute mb-0.5">FROM</p>}
                        {product.sale_active && product.sale_price_usd ? (
                            <div className="flex items-baseline gap-2">
                                <span className="font-display text-xl font-bold text-gold" data-testid={`product-price-${product.slug}`}>
                                    {formatPrice(product.sale_price_usd)}
                                </span>
                                <span className="text-xs font-mono text-ink-mute line-through">{formatPrice(product.price_usd)}</span>
                            </div>
                        ) : (
                            <span className="font-display text-xl font-bold text-ink" data-testid={`product-price-${product.slug}`}>
                                {formatPrice(product.price_usd)}
                            </span>
                        )}
                        {product.price_per_iu_usd && (
                            <span className="block mt-0.5 text-[10px] font-mono text-neon" data-testid={`product-iu-${product.slug}`}>
                                ≈ ${product.price_per_iu_usd.toFixed(2)} / IU
                            </span>
                        )}
                    </div>
                    <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-neon link-reveal">
                        {soldOut ? "NOTIFY" : "View →"}
                    </span>
                </div>
            </div>
        </Link>
    );
};

// Skeleton placeholder shown while /api/products is loading for the first time —
// paints instantly so the catalog grid never feels empty on slow networks / cold mobile.
export const ProductCardSkeleton = () => (
    <div className="border border-ink bg-ink-surface flex flex-col relative overflow-hidden animate-pulse" data-testid="product-card-skeleton" aria-hidden="true">
        <div className="relative aspect-[4/5] bg-ink-elevated border-b border-ink flex items-center justify-center">
            <div className="w-10 h-10 border-2 border-neon/20 border-t-neon/60 rounded-full animate-spin" />
            <div className="absolute bottom-3 left-3 w-16 h-4 bg-ink-surface/60" />
        </div>
        <div className="p-5 sm:p-6 flex-1 flex flex-col gap-3">
            <div className="h-3 w-24 bg-ink-elevated" />
            <div className="h-5 w-4/5 bg-ink-elevated" />
            <div className="h-3 w-full bg-ink-elevated" />
            <div className="h-3 w-3/5 bg-ink-elevated" />
            <div className="mt-auto flex items-baseline justify-between pt-4 border-t border-ink">
                <div className="h-6 w-20 bg-ink-elevated" />
                <div className="h-3 w-10 bg-ink-elevated" />
            </div>
        </div>
    </div>
);
